import "../../assets/css/global.scss";

import React, { useCallback, useState, useRef} from "react";
import type { FC, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import cn from "classnames";
import { useOnClickOutside, ToasterManager } from "@wf/components";
import { Header, Footer, MainContainer, SideBar, LanguageBar, UserBar, NavBar, LoadingLogo } from "./components";
import { useLayout, useAuthConfig, useColors } from "./hooks";
import { CRETE_ACCOUNT_LINK } from "../../constants";
import { tabletLandscapeMaxWidth } from "../../constants/viewports";
import {handleLinkClick} from "../../helpers";
import styles from "./TradeLayout.module.scss";

const TradeLayout: FC<PropsWithChildren> = ({ children }) => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const { i18n, t } = useTranslation();
  const sidebarRef = useRef();
  const isDesktop = useMediaQuery({ minWidth: tabletLandscapeMaxWidth + 1 });

  // @ts-ignore
  const { theme, logos, footerMenu, menu, languages, layoutLng, helpDesks } = useLayout(i18n.language, t);
  const { isKeycloakInitialized, authConfig } = useAuthConfig();
  const colorsApplied = useColors({ primaryColorHex: theme?.colors?.brandPrimary });

  const toggleSidebar = useCallback(() => setSidebarVisible(current => !current), []);

  const handleLanguageChange = useCallback(
      (l: string) => {
      i18n.changeLanguage(l);
    },
    [i18n]
  );

  useOnClickOutside(
      // @ts-ignore
    { current: sidebarRef.current },
    () => {
      setSidebarVisible(false);
    },
    { ignoreClassNames: ["wf-sidebar-ignore"] }
  );

  return (
      languages ?
      <>
        {(!colorsApplied || !isKeycloakInitialized) && <LoadingLogo />}

        <div className={cn({ [styles.hideContent]: !colorsApplied })}>
          <Header
            mainLogo={logos.main}
            secondaryLogo={logos.country}
            onMobileMenuClick={toggleSidebar}
            handleLinkClick={handleLinkClick}
            sidebarIsOpened={sidebarVisible}
          >
            <UserBar
              {...authConfig}
              translate={t}
              createAccountLink={CRETE_ACCOUNT_LINK}
              className="wf-sidebar-ignore"
              data-matomo-mask
            />
            {languages.length > 1 && (
              <LanguageBar
                languages={languages}
                onChange={handleLanguageChange}
                selected={layoutLng}
                className="wf-sidebar-ignore"
              />
            )}
            <NavBar menu={menu} handleLinkClick={handleLinkClick} />
          </Header>

          <MainContainer className={styles.mainContent}>
            {!isDesktop && (
              <SideBar
                menu={menu}
                isOpened={sidebarVisible}
                handleClose={toggleSidebar}
                handleLinkClick={handleLinkClick}
                mainLogo={logos.main}
                //@ts-ignore
                ref={sidebarRef}
              >
                <UserBar
                  {...authConfig}
                  translate={t}
                  createAccountLink={CRETE_ACCOUNT_LINK}
                  className={styles.sidebarUserBar}
                  data-matomo-mask
                />
                {languages.length > 1 && (
                  <LanguageBar
                    languages={languages}
                    onChange={handleLanguageChange}
                    selected={layoutLng}
                    showFlag={false}
                  />
                )}
              </SideBar>
            )}
            {children}
          </MainContainer>
          <Footer menu={footerMenu} handleLinkClick={handleLinkClick} translate={t} />
        </div>
      <ToasterManager />
    </> : <LoadingLogo />
  );
};

export default TradeLayout;
